import { useTheme } from "@mui/material";
import {
  useLicensingServiceV1,
  useUserManagementServiceV1,
} from "@rw1/api-services";
import { Licensing_V1, UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef } from "react";
import { Trans } from "react-i18next";
import { ALL_RELEASE_CHANNELS_QUERY_KEY } from "../hooks/useAllExperiences";
import { LICENSE_ALLOCATIONS_QUERY_KEY } from "../hooks/useExternalExperiences";
import { useLocalization } from "../hooks/useLocalization";
import { INCOMING_LICENSES_QUERY_KEY } from "../pages/experiences/hooks/useIncomingLicenses";
import { USERS_FOR_RELEASE_CHANNEL_QUERY_KEY } from "../pages/experiences/hooks/useUsersForReleaseChannel";
import {
  TAccessSource,
  TExperienceUserAccessRow,
} from "../pages/experiences/types";
import { ALL_LICENSES_QUERY_KEY } from "../pages/licenses/hooks/useExternalLicenses";
import { ConfirmationDialog } from "./ConfirmationDialog";

interface Props {
  open: boolean;
  ctx: {
    row: TExperienceUserAccessRow;
    source: TAccessSource;
    releaseChannelId: string;
    experienceName: string;
    isExternal?: boolean;
    licenseId?: string;
  } | null;
  onClose: () => void;
}

export const RemoveAccessDialog: React.FC<Props> = ({ open, ctx, onClose }) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const userManagementService = useUserManagementServiceV1();
  const licensingService = useLicensingServiceV1();

  const ctxRef = useRef(ctx);

  useEffect(() => {
    if (open && ctx) {
      ctxRef.current = ctx;
    }
  }, [open, ctx]);

  const displayCtx = open ? ctx : ctxRef.current;

  const mutation = useMutation({
    mutationFn: () =>
      userManagementService.removeReleaseChannelFromUser(
        displayCtx!.row.userId,
        displayCtx!.releaseChannelId,
      ),
    onMutate: async () => {
      const queryKey = [
        USERS_FOR_RELEASE_CHANNEL_QUERY_KEY,
        displayCtx!.releaseChannelId,
      ];
      await queryClient.cancelQueries({ queryKey });

      const previousUsers =
        queryClient.getQueryData<UserManagement_V1.UserAccessData[]>(queryKey);

      if (previousUsers) {
        queryClient.setQueryData<UserManagement_V1.UserAccessData[]>(
          queryKey,
          previousUsers.filter(
            (u) => !(u.userId === displayCtx!.row.userId && !u.userGroupId),
          ),
        );
      }

      return { previousUsers };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(
          [USERS_FOR_RELEASE_CHANNEL_QUERY_KEY, displayCtx!.releaseChannelId],
          context.previousUsers,
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          USERS_FOR_RELEASE_CHANNEL_QUERY_KEY,
          displayCtx!.releaseChannelId,
        ],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_RELEASE_CHANNELS_QUERY_KEY],
      });
    },
    onSuccess: () => {
      onClose();
    },
  });

  const externalMutation = useMutation({
    mutationFn: () =>
      licensingService.deallocateLicenseUserSeat(
        displayCtx!.licenseId!,
        displayCtx!.row.userId,
      ),
    onMutate: async () => {
      const queryKey = [LICENSE_ALLOCATIONS_QUERY_KEY, displayCtx!.licenseId];
      await queryClient.cancelQueries({ queryKey });

      const previousAllocations =
        queryClient.getQueryData<Licensing_V1.LicenseUserAllocation[]>(
          queryKey,
        );

      if (previousAllocations) {
        queryClient.setQueryData<Licensing_V1.LicenseUserAllocation[]>(
          queryKey,
          previousAllocations.filter(
            (a) => a.userId !== displayCtx!.row.userId,
          ),
        );
      }

      return { previousAllocations };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousAllocations) {
        queryClient.setQueryData(
          [LICENSE_ALLOCATIONS_QUERY_KEY, displayCtx!.licenseId],
          context.previousAllocations,
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [LICENSE_ALLOCATIONS_QUERY_KEY, displayCtx!.licenseId],
      });
      void queryClient.invalidateQueries({
        queryKey: [INCOMING_LICENSES_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_LICENSES_QUERY_KEY],
      });
    },
    onSuccess: () => {
      onClose();
    },
  });

  if (!displayCtx) return null;

  const { row, experienceName, isExternal } = displayCtx;
  const activeMutation = isExternal ? externalMutation : mutation;

  const handleConfirm = () => {
    activeMutation.mutate();
  };

  const handleCancel = () => {
    activeMutation.reset();
    onClose();
  };

  return (
    <ConfirmationDialog.Root maxWidth="xs" onClose={handleCancel} open={open}>
      <ConfirmationDialog.Title iconColor={theme.colors.orange} showIcon={true}>
        {t("components.removeAccessDialog.title")}
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content error={activeMutation.error}>
        <ConfirmationDialog.Text>
          <Trans
            i18nKey="components.removeAccessDialog.message"
            values={{
              userName: row.fullName,
              experienceName,
            }}
          />
        </ConfirmationDialog.Text>
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions>
        <ConfirmationDialog.Button onClick={handleCancel} variant="contained">
          {t("components.removeAccessDialog.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          isLoading={activeMutation.isPending}
          loadingText={t("components.removeAccessDialog.removing")}
          onClick={handleConfirm}
          variant="secondary"
        >
          {t("components.removeAccessDialog.remove")}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};
